import React, { useEffect, useState, useCallback } from 'react';
import { RequireRole } from '../../components/CallcenterV2/RequireRole';
import { ccv2Fetch } from '../../services/callcenterV2';
import '../../components/CallcenterV2/ccv2-components.css';
import './config-styles.css';

const UNIT_LABELS = { seconds: 'segundos', percent: '%', count: 'contagem', ratio: 'razão', ms: 'ms' };
const CATEGORY_LABELS = { volume: 'Volume', tma: 'TMA', tme: 'TME', sla: 'SLA', agent: 'Agente', queue: 'Fila', tenant: 'Tenant', custom: 'Personalizado' };
const AGGREGATIONS = ['sum', 'avg', 'min', 'max', 'count', 'last'];
const UNITS = ['seconds', 'percent', 'count', 'ratio', 'ms'];
const CATEGORIES = Object.keys(CATEGORY_LABELS);

function TargetModal({ metric, onClose, onSaved }) {
  const [targetValue, setTargetValue] = useState('');
  const [warningValue, setWarningValue] = useState('');
  const [criticalValue, setCriticalValue] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await ccv2Fetch('/v2/callcenter/metrics/targets', {
        method: 'POST',
        body: JSON.stringify({
          idMetricCatalog: metric.id_metric_catalog,
          scopeType: 'global',
          targetValue: parseFloat(targetValue) || null,
          warningValue: warningValue !== '' ? parseFloat(warningValue) : null,
          criticalValue: criticalValue !== '' ? parseFloat(criticalValue) : null,
        }),
      });
      onSaved();
    } catch (e) {
      alert(`Erro: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ccv2-p5-modal-overlay" onClick={onClose}>
      <div className="ccv2-p5-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ccv2-p5-modal-header">
          <h3 className="ccv2-p5-modal-title">Definir Meta · <code style={{ fontSize: '0.85rem' }}>{metric.metric_key}</code></h3>
          <button className="ccv2-p5-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="ccv2-p5-modal-body">
          <p style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
            <strong>{metric.label_pt}</strong>
            {metric.description && <span style={{ color: '#64748b', marginLeft: '0.5rem' }}>{metric.description}</span>}
          </p>
          <div className="ccv2-p5-field">
            <label className="ccv2-p5-label">Valor da Meta ({UNIT_LABELS[metric.unit] || metric.unit || 'unidade'})</label>
            <input className="ccv2-p5-input" type="number" value={targetValue} onChange={(e) => setTargetValue(e.target.value)} style={{ width: '100%' }} placeholder="ex: 80" />
          </div>
          <div className="ccv2-p5-field">
            <label className="ccv2-p5-label">Aviso (warning) — opcional</label>
            <input className="ccv2-p5-input" type="number" value={warningValue} onChange={(e) => setWarningValue(e.target.value)} style={{ width: '100%' }} placeholder="ex: 70" />
          </div>
          <div className="ccv2-p5-field">
            <label className="ccv2-p5-label">Crítico — opcional</label>
            <input className="ccv2-p5-input" type="number" value={criticalValue} onChange={(e) => setCriticalValue(e.target.value)} style={{ width: '100%' }} placeholder="ex: 60" />
          </div>
          <p className="ccv2-p5-help">Meta aplicada globalmente. Histórico preservado por versionamento.</p>
        </div>
        <div className="ccv2-p5-modal-footer">
          <button className="ccv2-p5-btn ccv2-p5-btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="ccv2-p5-btn ccv2-p5-btn-primary" disabled={!targetValue || saving} onClick={save}>
            {saving ? '⌛' : '🎯'} Salvar Meta
          </button>
        </div>
      </div>
    </div>
  );
}

function EditMetricModal({ metric, onClose, onSaved }) {
  const [form, setForm] = useState({
    labelPt: metric.label_pt || '',
    labelEn: metric.label_en || '',
    unit: metric.unit || 'count',
    aggregation: metric.aggregation || 'sum',
    category: metric.category || 'volume',
    isActive: metric.is_active !== false,
    sortOrder: metric.sort_order ?? 0,
    description: metric.description || '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await ccv2Fetch(`/v2/callcenter/metrics/catalog/${metric.id_metric_catalog}`, {
        method: 'PATCH',
        body: JSON.stringify({
          labelPt: form.labelPt,
          labelEn: form.labelEn,
          unit: form.unit,
          aggregation: form.aggregation,
          category: form.category,
          isActive: form.isActive,
          sortOrder: parseInt(form.sortOrder, 10),
          description: form.description,
        }),
      });
      onSaved();
    } catch (e) {
      alert(`Erro: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const h = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="ccv2-p5-modal-overlay" onClick={onClose}>
      <div className="ccv2-p5-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ccv2-p5-modal-header">
          <h3 className="ccv2-p5-modal-title">Editar Métrica · <code style={{ fontSize: '0.85rem' }}>{metric.metric_key}</code></h3>
          <button className="ccv2-p5-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="ccv2-p5-modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="ccv2-p5-field">
              <label className="ccv2-p5-label">Label PT</label>
              <input className="ccv2-p5-input" value={form.labelPt} onChange={(e) => h('labelPt', e.target.value)} style={{ width: '100%' }} />
            </div>
            <div className="ccv2-p5-field">
              <label className="ccv2-p5-label">Label EN</label>
              <input className="ccv2-p5-input" value={form.labelEn} onChange={(e) => h('labelEn', e.target.value)} style={{ width: '100%' }} />
            </div>
            <div className="ccv2-p5-field">
              <label className="ccv2-p5-label">Unidade</label>
              <select className="ccv2-p5-select" value={form.unit} onChange={(e) => h('unit', e.target.value)} style={{ width: '100%' }}>
                {UNITS.map((u) => <option key={u} value={u}>{UNIT_LABELS[u] || u}</option>)}
              </select>
            </div>
            <div className="ccv2-p5-field">
              <label className="ccv2-p5-label">Agregação</label>
              <select className="ccv2-p5-select" value={form.aggregation} onChange={(e) => h('aggregation', e.target.value)} style={{ width: '100%' }}>
                {AGGREGATIONS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="ccv2-p5-field">
              <label className="ccv2-p5-label">Categoria</label>
              <select className="ccv2-p5-select" value={form.category} onChange={(e) => h('category', e.target.value)} style={{ width: '100%' }}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
              </select>
            </div>
            <div className="ccv2-p5-field">
              <label className="ccv2-p5-label">Ordem</label>
              <input className="ccv2-p5-input" type="number" value={form.sortOrder} onChange={(e) => h('sortOrder', e.target.value)} style={{ width: '100%' }} />
            </div>
          </div>
          <div className="ccv2-p5-field">
            <label className="ccv2-p5-label">Descrição</label>
            <input className="ccv2-p5-input" value={form.description} onChange={(e) => h('description', e.target.value)} style={{ width: '100%' }} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginTop: '0.5rem' }}>
            <input type="checkbox" checked={form.isActive} onChange={(e) => h('isActive', e.target.checked)} />
            <span className="ccv2-p5-label" style={{ margin: 0 }}>Ativo</span>
          </label>
        </div>
        <div className="ccv2-p5-modal-footer">
          <button className="ccv2-p5-btn ccv2-p5-btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="ccv2-p5-btn ccv2-p5-btn-primary" disabled={saving} onClick={save}>
            {saving ? '⌛' : '💾'} Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function MetricsCatalogAdmin() {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [sortField, setSortField] = useState('sort_order');
  const [editMetric, setEditMetric] = useState(null);
  const [targetMetric, setTargetMetric] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `/v2/callcenter/metrics/catalog${includeInactive ? '?includeInactive=true' : ''}`;
      const data = await ccv2Fetch(url);
      setMetrics(Array.isArray(data) ? data : (data?.data || []));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [includeInactive]);

  useEffect(() => { load(); }, [load]);

  const handleSeedDefaults = async () => {
    try {
      const data = await ccv2Fetch('/v2/callcenter/metrics/catalog/seed-defaults');
      setSuccess(`Seed aplicado: ${data?.inserted ?? 0} métrica(s) inserida(s).`);
      load();
      setTimeout(() => setSuccess(null), 4000);
    } catch (e) { setError(e.message); }
  };

  const filtered = metrics
    .filter((m) => !filterCategory || m.category === filterCategory)
    .filter((m) => !filterSearch || (m.metric_key + ' ' + m.label_pt + ' ' + m.label_en).toLowerCase().includes(filterSearch.toLowerCase()))
    .sort((a, b) => {
      if (sortField === 'sort_order') return (a.sort_order ?? 99) - (b.sort_order ?? 99);
      if (sortField === 'metric_key') return (a.metric_key || '').localeCompare(b.metric_key || '');
      if (sortField === 'category') return (a.category || '').localeCompare(b.category || '');
      return 0;
    });

  return (
    <RequireRole roles={['manager', 'admin', 'root']}>
      <div className="ccv2-page-container ccv2-p5-container">
        <div className="ccv2-page-header">
          <div className="ccv2-page-title-area">
            <h1>Catálogo de Métricas</h1>
            <p>Configure métricas ativas, labels, metas e thresholds</p>
          </div>
          <div className="ccv2-page-actions">
            <button className="ccv2-p5-btn ccv2-p5-btn-secondary" onClick={load}>🔄</button>
            <button className="ccv2-p5-btn ccv2-p5-btn-primary" onClick={handleSeedDefaults}>🌱 Reaplicar Seed</button>
          </div>
        </div>

        {success && <div className="ccv2-p5-success">{success}</div>}
        {error && <div className="ccv2-p5-error">⚠️ {error}</div>}

        <div className="ccv2-p5-filters">
          <input className="ccv2-p5-input" placeholder="Buscar métrica..." value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} style={{ maxWidth: 200 }} />
          <select className="ccv2-p5-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            <option value="">Todas as categorias</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select className="ccv2-p5-select" value={sortField} onChange={(e) => setSortField(e.target.value)}>
            <option value="sort_order">Ordenar por: Ordem</option>
            <option value="metric_key">Ordenar por: Chave</option>
            <option value="category">Ordenar por: Categoria</option>
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#64748b' }}>
            <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
            Incluir inativas
          </label>
          <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{filtered.length} métrica(s)</span>
        </div>

        {loading ? (
          <div className="ccv2-config-loading"><div className="ccv2-spinner" /></div>
        ) : (
          <div className="ccv2-config-card">
            <div className="ccv2-config-table-wrapper">
              <table className="ccv2-config-table">
                <thead>
                  <tr>
                    <th>Chave</th>
                    <th>Label PT</th>
                    <th>Categoria</th>
                    <th>Unidade</th>
                    <th>Agregação</th>
                    <th>Meta</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                      Nenhuma métrica. Clique em "Reaplicar Seed" para popular.
                    </td></tr>
                  ) : filtered.map((m) => (
                    <tr key={m.id_metric_catalog} style={{ opacity: m.is_active === false ? 0.55 : 1 }}>
                      <td><code style={{ fontSize: '0.8rem' }}>{m.metric_key}</code></td>
                      <td style={{ fontWeight: 600 }}>{m.label_pt || '—'}</td>
                      <td><span style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: '0.75rem' }}>{CATEGORY_LABELS[m.category] || m.category}</span></td>
                      <td style={{ fontSize: '0.8rem' }}>{UNIT_LABELS[m.unit] || m.unit || '—'}</td>
                      <td><code style={{ fontSize: '0.75rem' }}>{m.aggregation || '—'}</code></td>
                      <td style={{ fontSize: '0.8rem' }}>{m.target_default != null ? `${m.target_default} ${UNIT_LABELS[m.unit] || ''}` : '—'}</td>
                      <td>
                        <span className={`ccv2-p5-badge ${m.is_active !== false ? 'ccv2-p5-badge-active' : 'ccv2-p5-badge-inactive'}`}>{m.is_active !== false ? 'Ativa' : 'Inativa'}</span>
                        {!m.tenant_id && <span className="ccv2-p5-badge ccv2-p5-badge-global" style={{ marginLeft: 4 }}>Global</span>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.35rem' }}>
                          <button className="ccv2-p5-btn ccv2-p5-btn-warning" style={{ fontSize: '0.72rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => setEditMetric(m)} title="Editar métrica">✏️</button>
                          <button className="ccv2-p5-btn ccv2-p5-btn-primary" style={{ fontSize: '0.72rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => setTargetMetric(m)} title="Definir meta">🎯</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {editMetric && (
          <EditMetricModal
            metric={editMetric}
            onClose={() => setEditMetric(null)}
            onSaved={() => { setEditMetric(null); load(); setSuccess('Métrica atualizada.'); setTimeout(() => setSuccess(null), 3000); }}
          />
        )}

        {targetMetric && (
          <TargetModal
            metric={targetMetric}
            onClose={() => setTargetMetric(null)}
            onSaved={() => { setTargetMetric(null); setSuccess('Meta definida com sucesso.'); setTimeout(() => setSuccess(null), 3000); }}
          />
        )}
      </div>
    </RequireRole>
  );
}

export default MetricsCatalogAdmin;
